import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Spinner,
  Switch,
  Text,
  Textarea,
  Tooltip,
  VStack,
  Circle,
} from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import { AddIcon, CheckIcon, CloseIcon, DeleteIcon, EditIcon, SearchIcon } from "@chakra-ui/icons";
import { FiFilter } from "react-icons/fi";
import { MemoryFactProto, MemorySource } from "build-ts/memory";
import { deleteMemoryFact, listMemoryFacts, saveMemoryFact } from "services/MemoryFactService";
import showToast from "hooks/useCustomToast";
import { useUser } from "context/UserContext";

type FilterMode = "all" | "active" | "inactive";

interface AnalystMemoryFactsComponentProps {
  analystId: string | undefined;
}

const AnalystMemoryFactsComponent: React.FC<AnalystMemoryFactsComponentProps> = ({ analystId }) => {
  const pathname = useLocation().pathname;
  const { userId } = useUser();
  const [facts, setFacts] = useState<MemoryFactProto[]>([]);
  const [loading, setLoading] = useState(false);
  const [addText, setAddText] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const addInputRef = useRef<HTMLTextAreaElement>(null);

  // Fetch all memory facts for the analyst and update cache
  const getAllMemoryFacts = async () => {
    if (!analystId) return;
    setLoading(true);
    try {
      const result = await listMemoryFacts(analystId);
      setFacts(result);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setEditingId(null);
    setAddText("");
    if (!pathname.includes("memory")) return;
    getAllMemoryFacts();
  }, [analystId, pathname]);

  const handleAdd = async () => {
    if (!analystId || !userId || !addText.trim()) return;
    setAdding(true);
    try {
      const created = await saveMemoryFact(
        analystId,
        userId,
        MemoryFactProto.create({
          text: addText.trim(),
          enabled: true,
          source: MemorySource.MANUAL,
          added_by: userId
        }),
      );
      if (created) {
        const next = [...facts, created];
        setFacts(next);
        setAddText("");
        showToast("Memory fact added", "", "success");
      }
    } finally {
      setAdding(false);
    }
  };

  const handleToggleEnabled = async (fact: MemoryFactProto) => {
    if (!fact.id || !analystId || !userId) return;
    setTogglingId(fact.id);
    try {
      const updated = await saveMemoryFact(
        analystId,
        userId,
        MemoryFactProto.create({ id: fact.id, enabled: !fact.enabled }),
      );
      if (updated) {
        const next = facts.map((f) => (f.id === fact.id ? updated : f));
        setFacts(next);
      }
    } finally {
      setTogglingId(null);
    }
  };

  const handleStartEdit = (fact: MemoryFactProto) => {
    setEditingId(fact.id ?? null);
    setEditText(fact.text ?? "");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const handleSaveEdit = async (fact: MemoryFactProto) => {
    if (!userId || !fact.id || !analystId) return;
    if (!editText.trim()) {
      showToast("Text cannot be empty", "", "warning");
      return;
    }
    setSavingId(fact.id);
    try {
      const updated = await saveMemoryFact(
        analystId,
        userId,
        MemoryFactProto.create({
          id: fact.id,
          text: editText.trim(),
          enabled: fact.enabled,
          source: fact.source,
        }),
      );
      if (updated) {
        const next = facts.map((f) => (f.id === fact.id ? updated : f));
        setFacts(next);
        setEditingId(null);
        setEditText("");
        showToast("Memory fact updated", "", "success");
      }
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (factId: string) => {
    if (!analystId) return;
    setDeletingId(factId);
    try {
      const ok = await deleteMemoryFact(factId);
      if (ok) {
        const next = facts.filter((f) => f.id !== factId);
        setFacts(next);
        showToast("Memory fact deleted", "", "success");
      }
    } finally {
      setDeletingId(null);
    }
  };

  if (!analystId) return null;

  const enabledCount = facts.filter((f) => f.enabled).length;

  const visibleFacts = facts.filter((f) => {
    const matchesFilter =
      filterMode === "all" ||
      (filterMode === "active" && f.enabled) ||
      (filterMode === "inactive" && !f.enabled);
    const matchesSearch =
      !searchText.trim() ||
      (f.text ?? "").toLowerCase().includes(searchText.trim().toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const filterLabel: Record<FilterMode, string> = {
    all: "All",
    active: "Active",
    inactive: "Inactive",
  };

  return (
    <Box height="100%" bg="white" p={4}>
      {/* Header */}
      <HStack justify="space-between" align="center" mb={1} spacing={4}>
        <HStack spacing={2} flexShrink={0}>
          {/* <Icon as={FaBrain} boxSize={4} color="purple.700" /> */}
          {facts.length > 0 && (
            <Badge colorScheme="purple" fontSize="xs" borderRadius="full" px={2}>
              {enabledCount}/{facts.length} active
            </Badge>
          )}
        </HStack>
        {/* Search Bar */}
        <InputGroup size="md" maxW="500px" flex="1">
          <InputLeftElement pointerEvents="none" height="full">
            <SearchIcon color="gray.400" boxSize={4} />
          </InputLeftElement>
          <Input
            placeholder="Search memory facts..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            bg="gray.50"
            border="1px"
            borderColor="gray.200"
            fontSize="sm"
            _hover={{
              borderColor: "gray.300",
              bg: "white"
            }}
            _focus={{
              borderColor: "purple.400",
              boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
              bg: "white"
            }}
            _placeholder={{
              color: "gray.400"
            }}
          />
        </InputGroup>
        <Menu closeOnSelect>
          <Tooltip label={`Filter: ${filterLabel[filterMode]}`}>
            <MenuButton
              as={IconButton}
              aria-label="Filter memory facts"
              icon={<Icon as={FiFilter} boxSize={5} />}
              size="sm"
              variant={filterMode !== "all" ? "solid" : "ghost"}
              colorScheme={filterMode !== "all" ? "purple" : "gray"}
            />
          </Tooltip>
          <MenuList minW="130px" fontSize="sm">
            <MenuItem
              onClick={() => setFilterMode("all")}
              fontWeight={filterMode === "all" ? "600" : "normal"}
            >
              All ({facts.length})
            </MenuItem>
            <MenuDivider />
            <MenuItem
              onClick={() => setFilterMode("active")}
              fontWeight={filterMode === "active" ? "600" : "normal"}
            >
              Active ({enabledCount})
            </MenuItem>
            <MenuItem
              onClick={() => setFilterMode("inactive")}
              fontWeight={filterMode === "inactive" ? "600" : "normal"}
            >
              Inactive ({facts.length - enabledCount})
            </MenuItem>
          </MenuList>
        </Menu>
      </HStack>
      <Divider borderColor="purple.600" />

      {/* Add fact row */}
      <Box maxW="900px" w="100%" mx="auto" display="flex" flexDirection="column" flex={1} minH={0}>
        <HStack spacing={2} mb={3} align="flex-end">
          <Textarea
            ref={addInputRef}
            size="sm"
            value={addText}
            onChange={(e) => setAddText(e.target.value)}
            placeholder="Add a memory fact..."
            rows={3}
            resize="none"
            minH="60px"
            _focus={{ borderColor: "purple.500", boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)" }}
            _hover={{ bg: "white" }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleAdd();
              }
            }}
          />
          <Tooltip label="Add memory fact">
            <IconButton
              aria-label="Add memory fact"
              icon={adding ? <Spinner size="xs" /> : <AddIcon />}
              size="sm"
              colorScheme="purple"
              variant="solid"
              isDisabled={!addText.trim() || adding}
              onClick={handleAdd}
            />
          </Tooltip>
        </HStack>

        {/* List */}
        {loading ? (
          <Flex justify="center" py={4}>
            <Spinner size="sm" color="purple.500" />
          </Flex>
        ) : facts.length === 0 ? (
          <Text fontSize="xs" color="gray.400" textAlign="center" py={2} mb={0}>
            No memory facts yet. Add one above to give this analyst persistent guidance.
          </Text>
        ) : visibleFacts.length === 0 ? (
          <Text fontSize="xs" color="gray.400" textAlign="center" py={2} mb={0}>
            No facts match your search or filter.
          </Text>
        ) : (
          <VStack align="stretch" spacing={1} overflowY="auto" maxH="520px" mt={4}>
            {visibleFacts.map((fact, index) => {
              const isEditing = editingId === fact.id;
              const isSaving = savingId === fact.id;
              const isDeleting = deletingId === fact.id;
              const isToggling = togglingId === fact.id;

              return (
                <Box
                  key={fact.id}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor={fact.enabled ? "purple.100" : "gray.200"}
                  bg={fact.enabled ? "purple.50" : "gray.50"}
                  px={3}
                  py={2}
                  opacity={fact.enabled ? 1 : 0.65}
                  transition="all 0.15s"
                >
                  {isEditing ? (
                    <VStack align="stretch" spacing={2}>
                      <Textarea
                        size="sm"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        rows={3}
                        resize="vertical"
                        autoFocus
                        _focus={{ borderColor: "purple.500", boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)" }}
                      />
                      <HStack justify="flex-end" spacing={2}>
                        <Button
                          size="xs"
                          variant="ghost"
                          leftIcon={<CloseIcon boxSize={2} />}
                          onClick={handleCancelEdit}
                          isDisabled={isSaving}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="xs"
                          colorScheme="purple"
                          leftIcon={isSaving ? <Spinner size="xs" /> : <CheckIcon boxSize={2} />}
                          onClick={() => fact && handleSaveEdit(fact)}
                          isDisabled={!editText.trim() || isSaving}
                        >
                          Save
                        </Button>
                      </HStack>
                    </VStack>
                  ) : (
                    <HStack justify="space-between" align="center" gap={2}>
                      <HStack spacing={2}>
                        <Circle
                          size="16px"
                          bg="purple.600"
                          color="white"
                          fontWeight="bold"
                          fontSize="xs"
                          flexShrink={0}
                        >
                          {index + 1}
                        </Circle>
                        <Text
                          fontSize="md"
                          flex={1}
                          color={fact.enabled ? "gray.800" : "gray.500"}
                          mb={0}
                          textAlign="left"
                          wordBreak="break-word"
                        >
                          {fact.text}
                        </Text>
                      </HStack>
                      <HStack spacing={2} flexShrink={0} mb={0}>
                        <Tooltip label={fact.enabled ? "Disable" : "Enable"} placement="top">
                          <Switch
                            size="sm"
                            colorScheme="purple"
                            isChecked={fact.enabled ?? false}
                            isDisabled={isToggling}
                            m={0}
                            mb={0}
                            p={1}
                            onChange={() => handleToggleEnabled(fact)}
                          />
                        </Tooltip>
                        <Tooltip label="Edit memory fact">
                          <IconButton
                            aria-label="Edit memory fact"
                            icon={<EditIcon boxSize={4} />}
                            size="xs"
                            variant="ghost"
                            colorScheme="purple"
                            onClick={() => handleStartEdit(fact)}
                          />
                        </Tooltip>
                        <Tooltip label="Delete memory fact">
                          <IconButton
                            aria-label="Delete memory fact"
                            icon={isDeleting ? <Spinner size="xs" /> : <DeleteIcon boxSize={3.5} />}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            isDisabled={isDeleting}
                            onClick={() => fact.id && handleDelete(fact.id)}
                          />
                        </Tooltip>
                      </HStack>
                    </HStack>
                  )}
                </Box>
              );
            })}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default AnalystMemoryFactsComponent;
